import { useEffect, useState } from 'react';

import {
  inviteToProperty,
  listShares,
  revokeShare,
  updateShareRole
} from '@vb-django/api/services/sharing';

import { cn } from '@utils/cn';

import { Button, HelperText, Icon, Sheet, Text } from '@ui-component-library';

import { usePropertySelection } from '../../context/PropertySelectionContext';

import type { Property, PropertyShare } from '@ts-types/property';

type ShareRole = 'view' | 'edit';

type PropertyShareSheetProps = {
  property: Property;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
};

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const RoleToggle = ({
  value,
  onChange,
  disabled
}: {
  value: ShareRole;
  onChange: (role: ShareRole) => void;
  disabled?: boolean;
}) => (
  <div className="flex flex-row gap-1">
    {(['view', 'edit'] as ShareRole[]).map((role) => (
      <Button
        key={role}
        variant={value === role ? 'secondary' : 'tertiary'}
        size="xsmall"
        disabled={disabled}
        onClick={() => onChange(role)}
      >
        {role === 'view' ? 'Visa' : 'Redigera'}
      </Button>
    ))}
  </div>
);

const PropertyShareSheet = ({ property, isOpen, onClose, className }: PropertyShareSheetProps) => {
  const { setAlertMessage } = usePropertySelection();

  const [shares, setShares] = useState<PropertyShare[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ShareRole>('view');
  const [error, setError] = useState<string | null>(null);
  const [isInviting, setIsInviting] = useState(false);

  useEffect(() => {
    if (!isOpen || !property?.ref) {
      return;
    }

    let active = true;
    listShares(property.ref).then((resp) => {
      if (active && resp.ok) {
        setShares(resp.data?.shares || []);
      }
    });

    return () => {
      active = false;
    };
  }, [isOpen, property?.ref]);

  if (!isOpen) {
    return null;
  }

  const handleInvite = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!isValidEmail(trimmed)) {
      setError('Ange en giltig e-postadress');
      return;
    }

    setError(null);
    setIsInviting(true);
    const resp = await inviteToProperty(property.ref, trimmed, role);
    setIsInviting(false);

    if (!resp.ok) {
      setError(resp.data?.error || 'Kunde inte skicka inbjudan');
      return;
    }

    setShares(resp.data?.shares || []);
    setEmail('');
    setRole('view');
  };

  const handleRoleChange = async (share: PropertyShare, newRole: ShareRole) => {
    if (share.role === newRole) {
      return;
    }
    const previous = shares;
    setShares((current) =>
      current.map((s) => (s.id === share.id ? { ...s, role: newRole } : s))
    );
    const resp = await updateShareRole(share.id, newRole);
    if (!resp.ok) {
      setShares(previous);
    }
  };

  const handleRevoke = (share: PropertyShare) => {
    setAlertMessage({
      title: 'Ta bort delning',
      subtitle: 'Är du säker på att du vill ta bort åtkomsten för',
      designation: share.email,
      isOpen: true,
      primaryActionText: 'Ta bort',
      onPrimaryAction: async () => {
        setAlertMessage(null);
        const resp = await revokeShare(share.id);
        if (resp.ok) {
          setShares((current) => current.filter((s) => s.id !== share.id));
        }
      },
      onClose: () => setAlertMessage(null)
    });
  };

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      className={cn('flex-col', className)}
      hideBackdrop={true}
    >
      <Sheet.Header>
        <Button variant="tertiary" size="icon" onClick={onClose}>
          <Icon name="ArrowShortSimple" className="color-charcoal-500" size="large" />
        </Button>

        <Text variant="text-lg/leading-normal/medium">Dela fastighet</Text>

        <Button variant="tertiary" size="icon" onClick={onClose}>
          <Icon name="CrossSimple" className="color-charcoal-500" size="large" />
        </Button>
      </Sheet.Header>

      <Sheet.Content className="flex flex-col gap-4 bg-[#F5F5F5]">
        <div className="rounded-lg-alt bg-white-50 flex flex-col gap-3 px-4 py-3">
          <Text variant="text-sm/leading-normal/medium">Bjud in via e-post</Text>

          <input
            type="email"
            value={email}
            placeholder="namn@exempel.se"
            onChange={(e) => setEmail(e.currentTarget.value)}
            className="rounded-lg border border-solid border-[#E2E8F0] bg-white px-3 py-2 text-sm outline-none focus:border-[#0F0F0F]"
          />

          <div className="flex flex-row items-center justify-between">
            <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
              Behörighet
            </Text>
            <RoleToggle value={role} onChange={setRole} disabled={isInviting} />
          </div>

          {error && (
            <Text
              variant="text-xs/leading-normal/normal"
              className="flex flex-row items-center gap-2 text-danger-500"
            >
              <Icon name="WarningFilled" className="fill-danger-500" /> {error}
            </Text>
          )}

          <Button variant="primary" size="small" fullWidth onClick={handleInvite} disabled={isInviting}>
            {isInviting ? 'Skickar...' : 'Bjud in'}
          </Button>
        </div>

        <HelperText
          label="Personen får ett mejl med en länk för att acceptera inbjudan. Inbjudan fungerar både för befintliga och nya användare."
          className="px-3 text-[#737373]"
        />

        <div className="flex flex-col gap-2">
          <Text variant="text-sm/leading-normal/medium" className="ml-4">
            Delad med
          </Text>

          {shares.length === 0 ? (
            <div className="rounded-lg-alt bg-white-50 px-4 py-3">
              <Text variant="text-sm/leading-normal/normal" className="text-[#737373]">
                Fastigheten är inte delad med någon ännu.
              </Text>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {shares.map((share) => (
                <div
                  key={share.id}
                  className="bg-white-50 flex w-full flex-col gap-2 rounded-lg px-3 py-2"
                >
                  <div className="flex min-w-0 flex-col gap-1">
                    <Text
                      variant="text-sm/leading-normal/medium"
                      className="truncate"
                    >
                      {share.invited_user_name || share.email}
                    </Text>
                    <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                      {share.status === 'pending' ? 'Väntar på svar' : 'Aktiv'}
                    </Text>
                  </div>

                  <div className="flex flex-row items-center justify-between gap-2">
                    <RoleToggle
                      value={share.role}
                      onChange={(newRole) => handleRoleChange(share, newRole)}
                    />
                    <Button variant="tertiary" size="icon" onClick={() => handleRevoke(share)}>
                      <Icon name="TrashBinSimple" size="large" className="fill-danger-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Sheet.Content>
    </Sheet>
  );
};

export { PropertyShareSheet };
